import get from 'lodash/get';

const TABLE_TO_API_COLUMN = {
  asset: 'target',
  confirmed: 'confirmed',
  create_date: 'date',
  cve: 'cve',
  cvss: 'cvss3',
  cwe: 'cwe',
  data: 'data',
  description: 'description',
  easeofresolution: 'easeofresolution',
  evidence: 'attachments_count',
  external_id: 'external_id',
  host__os: 'host_os',
  hostnames: 'hostnames',
  id: 'id',
  impact: 'impact',
  issuetracker: 'issuetracker',
  last_detected: 'last_detected',
  method: 'method',
  name: 'name',
  owasp: 'owasp',
  path: 'path',
  query: 'query',
  risk: 'risk',
  request: 'request',
  resolution: 'resolution',
  response: 'response',
  service__name: 'service',
  severity: 'severity',
  status: 'status',
  status_code: 'status_code',
  status_history: 'status_history',
  tool: 'tool',
  update_date: 'update_date',
  web: 'type',
  website: 'website',
  workspace_name: 'workspace_name'
};

const TABLE_TO_API_ORDER_FIELD = {
  easeofresolution: 'ease_of_resolution',
  host__os: 'target_host_os',
  host_os: 'target_host_os'
};

const SUPPORTED_VULN_COLUMNS = new Set([
  'id',
  'confirmed',
  'name',
  'severity',
  'tags',
  'risk',
  'cvss2',
  'cvss3',
  'cvss4',
  'hostnames',
  'date',
  'description',
  'data',
  'request',
  'resolution',
  'response',
  'cve',
  'cwe',
  'path',
  'owasp',
  'service',
  'tool',
  'status',
  'issuetracker',
  'target',
  'easeofresolution',
  'external_id',
  'method',
  'query',
  'status_code',
  'website',
  'host_os',
  'impact',
  'update_date',
  'type',
  'workspace_name'
]);

const UNSUPPORTED_VULN_QUERY_FIELDS = ['workspace_name', 'workspace__name'];
const REQUIRED_VULN_COLUMNS = ['id'];
// The backend omits these fields unless they are explicitly requested.
const OPT_IN_VULN_COLUMNS = new Set([
  'data',
  'description',
  'request',
  'resolution',
  'response'
]);

const isUnsupportedVulnQueryField = (field = '') => UNSUPPORTED_VULN_QUERY_FIELDS.includes(field);
const isSupportedVulnColumn = (column = '') => SUPPORTED_VULN_COLUMNS.has(column);
const getApiVulnColumn = (field = '') => TABLE_TO_API_COLUMN[field];
const getApiVulnOrderField = (field = '') => TABLE_TO_API_ORDER_FIELD[field] || field;

export const hasUnsupportedVisibleVulnColumns = (fields = {}) => Object.keys(fields).some((field) => {
  if (!get(fields, `${field}.visible`, false) || field === 'selected') return false;

  if (field.endsWith('_custom')) return true;

  const apiColumn = getApiVulnColumn(field);
  return !apiColumn || !isSupportedVulnColumn(apiColumn);
});

const sanitizeVulnQueryFields = (fields = []) => fields
  .filter((fieldData) => !isUnsupportedVulnQueryField(get(fieldData, 'field', '')));

const sanitizeVulnOrderFields = (fields = []) => sanitizeVulnQueryFields(fields)
  .map((fieldData) => ({
    ...fieldData,
    field: getApiVulnOrderField(get(fieldData, 'field', ''))
  }));

export const sanitizeVulnQueryParam = (queryParam = {}) => {
  const sanitizedQueryParam = { ...queryParam };

  if (Array.isArray(queryParam.order_by)) {
    sanitizedQueryParam.order_by = sanitizeVulnOrderFields(queryParam.order_by);
  }

  if (Array.isArray(queryParam.group_by)) {
    sanitizedQueryParam.group_by = sanitizeVulnQueryFields(queryParam.group_by);
  }

  return sanitizedQueryParam;
};

export const getVisibleVulnColumns = (fields = {}) => {
  const hasUnsupportedVisibleColumn = hasUnsupportedVisibleVulnColumns(fields);

  // If the UI needs a visible column the backend cannot return via `columns`
  // (for example a custom field),
  // we must skip the optimization and keep requesting the full payload.
  if (hasUnsupportedVisibleColumn) return [];

  const visibleColumns = Object.keys(fields)
    .filter((field) => get(fields, `${field}.visible`, false) && isSupportedVulnColumn(getApiVulnColumn(field)))
    .sort((a, b) => {
      const aOrder = get(fields, `${a}.order`, 0);
      const bOrder = get(fields, `${b}.order`, 0);
      return aOrder - bOrder;
    })
    .map((field) => getApiVulnColumn(field));

  return [...new Set(visibleColumns)];
};

const getVisibleSupportedVulnColumns = (fields = {}) => {
  const visibleColumns = Object.keys(fields)
    .filter((field) => get(fields, `${field}.visible`, false) && isSupportedVulnColumn(getApiVulnColumn(field)))
    .sort((a, b) => {
      const aOrder = get(fields, `${a}.order`, 0);
      const bOrder = get(fields, `${b}.order`, 0);
      return aOrder - bOrder;
    })
    .map((field) => getApiVulnColumn(field));

  return [...new Set(visibleColumns)];
};

const getVisibleOptInVulnColumns = (fields = {}) => Object.keys(fields)
  .filter((field) => get(fields, `${field}.visible`, false))
  .sort((a, b) => {
    const aOrder = get(fields, `${a}.order`, 0);
    const bOrder = get(fields, `${b}.order`, 0);
    return aOrder - bOrder;
  })
  .map((field) => getApiVulnColumn(field))
  .filter((column) => OPT_IN_VULN_COLUMNS.has(column));

const addVulnColumns = (queryParam, columns) => {
  if (columns.length === 0) return queryParam;

  const currentColumns = Array.isArray(queryParam.columns) ? queryParam.columns : [];

  return {
    ...queryParam,
    columns: [...new Set([...currentColumns, ...columns])]
  };
};

export const withVulnColumns = (queryParam, fields = {}, options = {}) => {
  if (!queryParam || Array.isArray(queryParam)) return queryParam;
  const sanitizedQueryParam = sanitizeVulnQueryParam(queryParam);

  if (options.keepFullPayload || hasUnsupportedVisibleVulnColumns(fields)) {
    const optInColumns = getVisibleOptInVulnColumns(fields);
    if (optInColumns.length === 0) return sanitizedQueryParam;

    const visibleColumns = getVisibleSupportedVulnColumns(fields);
    const groupByColumns = get(sanitizedQueryParam, 'group_by', [])
      .map((group) => get(group, 'field', ''))
      .filter(Boolean);

    return addVulnColumns(sanitizedQueryParam, [
      ...REQUIRED_VULN_COLUMNS,
      ...visibleColumns,
      ...groupByColumns
    ]);
  }

  const visibleColumns = getVisibleVulnColumns(fields);
  const groupByColumns = get(sanitizedQueryParam, 'group_by', [])
    .map((group) => get(group, 'field', ''))
    .filter(Boolean);
  if (visibleColumns.length === 0 && groupByColumns.length === 0) return sanitizedQueryParam;

  const columns = [...REQUIRED_VULN_COLUMNS, ...visibleColumns, ...groupByColumns];
  return addVulnColumns(sanitizedQueryParam, columns);
};
